import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@thinkalpha/ui-core/utils';
import * as React from 'react';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    Button,
    Popover,
    PopoverContent,
    PopoverTrigger,
    ScrollArea,
} from 'src/components/ui';

export interface ComboboxItem {
    value: string;
    label: string;
    isSelected: boolean;
}

interface ComboboxProps {
    children: React.ReactNode;
    commandEmptyText: string;
    disabled?: boolean;
    handleSelectItem: (item: ComboboxItem) => void;
    items: ComboboxItem[];
    onValueChange?: (value: string) => void;
    className?: string;
    triggerTitle?: string;
    value?: string;
    /**
     * Custom filter function for whether each command menu item should match the given search query.
     * It should return a number between 0 and 1, with 1 being the best match and 0 being hidden entirely.
     * By default, uses the `command-score` library.
     */
    comparator?: (value: string, search: string) => number;
    toggable?: boolean;
}

export function Combobox({
    children,
    commandEmptyText,
    disabled,
    handleSelectItem,
    items,
    onValueChange,
    className,
    triggerTitle,
    value,
    comparator,
    toggable = false,
}: ComboboxProps) {
    const [open, setOpen] = React.useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    disabled={disabled}
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn('w-[200px] justify-between', className)}
                    title={triggerTitle}
                >
                    {children}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0" align="start">
                <Command filter={comparator}>
                    <CommandInput placeholder="Search" onValueChange={onValueChange} value={value} />
                    <CommandEmpty>{commandEmptyText}</CommandEmpty>
                    <CommandGroup>
                        <ScrollArea className="h-72">
                            {items.map((item) => (
                                <CommandItem
                                    value={item.label + '||' + item.value} // enable searching the label but also allows duplicate labels
                                    key={item.value}
                                    onSelect={() => {
                                        if (item.isSelected && !toggable) return;
                                        handleSelectItem(item);
                                        setOpen(false);
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className={cn('mr-2', item.isSelected ? 'opacity-100' : 'opacity-0')}
                                    />

                                    {item.label}
                                </CommandItem>
                            ))}
                        </ScrollArea>
                    </CommandGroup>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
